/**
 * Radar Color Ramps
 * Shared color definitions for precipitation type visualization.
 * Used by both main thread and Web Worker.
 */

export const RAMP_LENGTH = 14;

/** Rain color ramp - weather.com style (green → yellow → orange → red → pink) */
export const RAIN_RAMP = new Uint8Array([
  80, 200, 80,    //  0: bright green
  50, 180, 50,    //  1: green
  30, 160, 30,    //  2: darker green
  20, 140, 20,    //  3: dark green
  180, 200, 30,   //  4: yellow-green
  240, 240, 0,    //  5: yellow
  255, 200, 0,    //  6: gold
  255, 150, 0,    //  7: orange
  255, 100, 0,    //  8: dark orange
  255, 50, 30,    //  9: red-orange
  240, 20, 50,    // 10: red
  210, 20, 90,    // 11: red-magenta
  190, 50, 130,   // 12: magenta
  220, 130, 180,  // 13: pink (extreme)
]);

/** Snow color ramp - blue tones (dark blue → light blue → white) */
export const SNOW_RAMP = new Uint8Array([
  30, 80, 160,    //  0: dark blue
  40, 100, 180,   //  1: blue
  55, 120, 200,   //  2: medium blue
  70, 140, 215,   //  3: blue
  90, 160, 230,   //  4: bright blue
  110, 180, 240,  //  5: sky blue
  135, 195, 248,  //  6: light blue
  160, 210, 252,  //  7: lighter blue
  180, 225, 255,  //  8: pale blue
  200, 238, 255,  //  9: very light blue
  220, 245, 255,  // 10: ice blue
  235, 250, 255,  // 11: near white blue
  245, 252, 255,  // 12: almost white
  255, 255, 255,  // 13: white (extreme)
]);

/** Mix color ramp - weather.com style (dark magenta → light pink) */
export const MIX_RAMP = new Uint8Array([
  140, 30, 100,   //  0: dark magenta
  165, 45, 115,   //  1: magenta
  185, 65, 135,   //  2: medium magenta
  205, 90, 155,   //  3: magenta-pink
  220, 115, 175,  //  4: pink-magenta
  235, 145, 195,  //  5: medium pink
  245, 175, 215,  //  6: pink
  250, 200, 230,  //  7: light pink
  252, 220, 242,  //  8: lighter pink
  254, 235, 250,  //  9: pale pink
  255, 245, 253,  // 10: very light pink
  255, 250, 254,  // 11: near white pink
  255, 253, 255,  // 12: almost white
  255, 255, 255,  // 13: white (extreme)
]);

/** Freezing rain color ramp - purple/violet tones (dark purple → light lavender) */
export const FREEZING_RAIN_RAMP = new Uint8Array([
  60, 20, 120,    //  0: dark purple
  80, 35, 145,    //  1: purple
  100, 50, 170,   //  2: medium purple
  120, 70, 190,   //  3: violet
  140, 90, 210,   //  4: bright violet
  160, 115, 225,  //  5: light violet
  180, 140, 235,  //  6: lavender
  195, 165, 245,  //  7: light lavender
  210, 185, 250,  //  8: pale lavender
  225, 205, 252,  //  9: very light lavender
  238, 225, 254,  // 10: near white lavender
  245, 238, 255,  // 11: white-lavender
  250, 248, 255,  // 12: almost white
  255, 255, 255,  // 13: white (extreme)
]);

/** Precip type enum values (must match RadarPrecipType) */
export const PRECIP_TYPE = {
  RAIN: 0,
  SNOW: 1,
  MIX: 2,
  FREEZING_RAIN: 3,
  ICE_PELLETS: 4,
} as const;

/** Get the color ramp for a precip type */
export function getRampForPrecipType(precipType: number): Uint8Array {
  switch (precipType) {
    case PRECIP_TYPE.SNOW: return SNOW_RAMP;
    case PRECIP_TYPE.MIX: return MIX_RAMP;
    case PRECIP_TYPE.ICE_PELLETS: return MIX_RAMP;
    case PRECIP_TYPE.FREEZING_RAIN: return FREEZING_RAIN_RAMP;
    default: return RAIN_RAMP;
  }
}
